import { addMonths, endOfDay, startOfDay } from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';

export const DEFAULT_MONTH_RANGE = 6;

/**
 * Returns the start or end of a calendar day in the given time zone as a UTC ISO string.
 *
 * The day is resolved in `timeZone` (not in the system or UTC zone), so the result is a real UTC instant.
 * This matches what `DatePickerField` produces when the same date is picked manually.
 * DST is handled by `date-fns-tz`.
 *
 * @param timeZone - IANA time zone identifier, e.g. `Europe/Kyiv`.
 * @param monthsAhead - Number of months to add to today's date. Defaults to `0` (today).
 * @param includeEndOfDay - If `true`, returns the last millisecond of the day (`23:59:59.999`)
 * instead of the start of the day (`00:00:00.000`). Defaults to `false`.
 * @returns UTC ISO 8601 string, e.g. `2026-10-22T21:00:00.000Z`.
 *
 * @example
 * // Today is 2026-10-23, time zone is Europe/Kyiv (UTC+3)
 * toZonedDayIso('Europe/Kyiv'); // '2026-10-22T21:00:00.000Z'
 * toZonedDayIso('Europe/Kyiv', 6, true); // '2027-04-23T20:59:59.999Z'
 */
export const toZonedDayIso = (timeZone: string, monthsAhead = 0, includeEndOfDay = false) => {
  const zonedDay = addMonths(toZonedTime(new Date(), timeZone), monthsAhead);
  const boundary = includeEndOfDay ? endOfDay(zonedDay) : startOfDay(zonedDay);

  return fromZonedTime(boundary, timeZone).toISOString();
};
